<script lang="ts" setup>
import request from "@/data/api/request";
import { loader } from "@/components/loader/loader";
import { toast } from "vue3-toastify";

definePage({
  meta: {
    action: "read",
    subject: "PackingSlip",
    navActiveLink: "packing-slip-settings",
  },
});

type CarrierSetting = { id?: string; code: string; name: string };
type AdministrationSetting = { id?: string; administration: number; name: string };
type TranssmartSetting = {
  id?: string;
  carrierCode: string;
  administration: number;
  shippingLocationId: string;
  service: string;
  bookingProfile: string;
};
type BookingProfileOption = { nr: number; value: string };
type ShippingLocationOption = {
  id: string;
  name?: string;
  addressLine1?: string;
  houseNo?: string;
  zipCode?: string;
  city?: string;
  country?: string;
  type?: string;
};
type BookingProfileItem = {
  nr: number;
  code: string;
  value: string;
  details: Record<string, any> | null;
};

const activeTab = ref("carriers");
const loading = ref(false);

const carriers = ref<CarrierSetting[]>([]);
const administrations = ref<AdministrationSetting[]>([]);
const transsmartMappings = ref<TranssmartSetting[]>([]);
const bookingProfiles = ref<BookingProfileOption[]>([]);
const shippingLocations = ref<ShippingLocationOption[]>([]);

function toBookingProfileItem(profile: BookingProfileOption): BookingProfileItem {
  const rawValue = profile?.value ?? "";

  if (rawValue.trim().startsWith("{")) {
    try {
      const parsed = JSON.parse(rawValue);
      const code = typeof parsed?.code === "string" ? parsed.code : rawValue;

      return {
        nr: profile.nr,
        code,
        value: rawValue,
        details: parsed,
      };
    } catch (_error) {
      // Fallback naar platte waarde
    }
  }

  return {
    nr: profile.nr,
    code: rawValue,
    value: rawValue,
    details: null,
  };
}

const bookingProfileItems = computed(() => bookingProfiles.value.map(toBookingProfileItem));

const carrierSelectItems = computed(() =>
  carriers.value.map((item) => ({
    title: item.name ? `${item.name} (${item.code})` : item.code,
    value: item.code,
  }))
);

const administrationSelectItems = computed(() =>
  administrations.value.map((item) => ({
    title: item.name
      ? `${item.name} (${item.administration})`
      : item.administration.toString(),
    value: item.administration,
  }))
);

const shippingLocationSelectItems = computed(() =>
  shippingLocations.value
    .filter((item) => (item.type || '').trim().toUpperCase() === 'SEND')
    .map((item) => ({
      title: `${item.name || 'Onbekend'} (${item.addressLine1 || '-'} ${item.houseNo || ''}, ${item.zipCode || '-'} ${item.city || '-'})`,
      value: item.id,
    }))
);

const selectedBookingProfile = computed(() =>
  bookingProfileItems.value.find((item) => item.code === mappingForm.value.bookingProfile)
);

const carrierForm = ref<CarrierSetting>({ code: "", name: "" });
const administrationForm = ref<AdministrationSetting>({ administration: 1, name: "" });
const mappingForm = ref<TranssmartSetting>({
  carrierCode: "",
  administration: 1,
  shippingLocationId: "",
  service: "",
  bookingProfile: "",
});

async function loadAll(): Promise<void> {
  loading.value = true;
  try {
    const [carrierResponse, administrationResponse, transsmartResponse] = await Promise.all([
      request.get("/packing-slip-settings/carriers"),
      request.get("/packing-slip-settings/administrations"),
      request.get("/packing-slip-settings/transsmart"),
    ]);

    carriers.value = carrierResponse.items || [];
    administrations.value = administrationResponse.items || [];
    transsmartMappings.value = transsmartResponse.items || [];

    const [bookingProfilesResponse, shippingLocationsResponse] = await Promise.all([
      request.get("/packing-slip-settings/transsmart/booking-profiles"),
      request.get("/packing-slip-settings/transsmart/shipping-locations"),
    ]);
    bookingProfiles.value = bookingProfilesResponse.items || [];
    shippingLocations.value = shippingLocationsResponse.items || [];
  } catch (error: any) {
    toast.error(error?.message || "Kon instellingen niet laden");
  } finally {
    loading.value = false;
  }
}

async function addCarrier(): Promise<void> {
  await request.post("/packing-slip-settings/carriers", {
    ...carrierForm.value,
    id: carrierForm.value.code,
  });
  carrierForm.value = { code: "", name: "" };
  await loadAll();
}

async function addAdministration(): Promise<void> {
  await request.post("/packing-slip-settings/administrations", {
    ...administrationForm.value,
    id: administrationForm.value.administration.toString(),
  });
  administrationForm.value = { administration: 1, name: "" };
  await loadAll();
}

async function addMapping(): Promise<void> {
  const id = `${mappingForm.value.carrierCode}-${mappingForm.value.administration}-${mappingForm.value.service}`;
  await request.post("/packing-slip-settings/transsmart", {
    ...mappingForm.value,
    id,
  });
  mappingForm.value = {
    carrierCode: "",
    administration: 1,
    shippingLocationId: "",
    service: "",
    bookingProfile: "",
  };
  await loadAll();
}

async function removeCarrier(item: CarrierSetting): Promise<void> {
  await request.delete(`/packing-slip-settings/carriers/${item.id}`);
  await loadAll();
}

async function removeAdministration(item: AdministrationSetting): Promise<void> {
  await request.delete(`/packing-slip-settings/administrations/${item.id}`);
  await loadAll();
}

async function removeMapping(item: TranssmartSetting): Promise<void> {
  await request.delete(`/packing-slip-settings/transsmart/${item.id}`);
  await loadAll();
}

onMounted(async () => {
  loader.stop();
  await loadAll();
});
</script>

<template>
  <VCard>
    <VCardTitle>Pakbon instellingen</VCardTitle>
    <VTabs v-model="activeTab">
      <VTab value="carriers">Carriers</VTab>
      <VTab value="administrations">Administraties</VTab>
      <VTab value="transsmart">Transsmart</VTab>
    </VTabs>

    <VWindow v-model="activeTab">
      <VWindowItem value="carriers">
        <VCardText>
          <VRow>
            <VCol cols="12" md="4"><VTextField v-model="carrierForm.code" label="Code" /></VCol>
            <VCol cols="12" md="6"><VTextField v-model="carrierForm.name" label="Naam" /></VCol>
            <VCol cols="12" md="2"><VBtn block color="primary" @click="addCarrier">Toevoegen</VBtn></VCol>
          </VRow>
          <VTable>
            <thead><tr><th>Code</th><th>Naam</th><th /></tr></thead>
            <tbody>
              <tr v-for="item in carriers" :key="item.id">
                <td>{{ item.code }}</td>
                <td>{{ item.name }}</td>
                <td><VBtn size="small" color="error" @click="removeCarrier(item)">Verwijderen</VBtn></td>
              </tr>
            </tbody>
          </VTable>
        </VCardText>
      </VWindowItem>

      <VWindowItem value="administrations">
        <VCardText>
          <VRow>
            <VCol cols="12" md="4"><VTextField v-model.number="administrationForm.administration" label="Administratie" type="number" /></VCol>
            <VCol cols="12" md="6"><VTextField v-model="administrationForm.name" label="Naam" /></VCol>
            <VCol cols="12" md="2"><VBtn block color="primary" @click="addAdministration">Toevoegen</VBtn></VCol>
          </VRow>
          <VTable>
            <thead><tr><th>Administratie</th><th>Naam</th><th /></tr></thead>
            <tbody>
              <tr v-for="item in administrations" :key="item.id">
                <td>{{ item.administration }}</td>
                <td>{{ item.name }}</td>
                <td><VBtn size="small" color="error" @click="removeAdministration(item)">Verwijderen</VBtn></td>
              </tr>
            </tbody>
          </VTable>
        </VCardText>
      </VWindowItem>

      <VWindowItem value="transsmart">
        <VCardText>
          <VRow>
            <VCol cols="12" md="3"><VSelect v-model="mappingForm.carrierCode" :items="carrierSelectItems" label="Carrier" /></VCol>
            <VCol cols="12" md="3"><VSelect v-model="mappingForm.administration" :items="administrationSelectItems" label="Administratie" /></VCol>
            <VCol cols="12" md="3">
              <VSelect
                v-model="mappingForm.shippingLocationId"
                :items="shippingLocationSelectItems"
                label="Verzendlocatie"
              />
            </VCol>
            <VCol cols="12" md="3"><VTextField v-model="mappingForm.service" label="Dienst" /></VCol>
            <VCol cols="12" md="2">
              <VSelect
                v-model="mappingForm.bookingProfile"
                :items="bookingProfileItems"
                item-title="code"
                item-value="code"
                label="Booking profile"
              />
            </VCol>
            <VCol cols="12" md="1"><VBtn block color="primary" @click="addMapping">+ </VBtn></VCol>
          </VRow>
          <VAlert
            v-if="selectedBookingProfile && selectedBookingProfile.details"
            type="info"
            variant="tonal"
            class="mb-4"
          >
            <div><strong>Gekozen code:</strong> {{ selectedBookingProfile.code }}</div>
            <div v-if="selectedBookingProfile.details.description">
              <strong>Omschrijving:</strong> {{ selectedBookingProfile.details.description }}
            </div>
            <div v-if="selectedBookingProfile.details.carrier">
              <strong>Carrier:</strong> {{ selectedBookingProfile.details.carrier }}
            </div>
            <div><strong>Details:</strong> {{ selectedBookingProfile.value }}</div>
          </VAlert>
          <VTable>
            <thead><tr><th>Carrier</th><th>Administratie</th><th>Verzendlocatie</th><th>Dienst</th><th>Booking profile</th><th /></tr></thead>
            <tbody>
              <tr v-for="item in transsmartMappings" :key="item.id">
                <td>{{ item.carrierCode }}</td>
                <td>{{ item.administration }}</td>
                <td>{{ item.shippingLocationId }}</td>
                <td>{{ item.service }}</td>
                <td>{{ item.bookingProfile }}</td>
                <td><VBtn size="small" color="error" @click="removeMapping(item)">Verwijderen</VBtn></td>
              </tr>
            </tbody>
          </VTable>
        </VCardText>
      </VWindowItem>
    </VWindow>

    <VCardText v-if="loading">Laden...</VCardText>
  </VCard>
</template>
